import React from 'react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { Loader2 } from 'lucide-react'
import { Button } from '@/shared/ui'
import type { OrderHistoryItem } from '@/shared/api'

const DASH = '—'

const hasValue = (value: string | null | undefined): value is string =>
  value !== null && value !== undefined && value.trim() !== ''

export interface OrderHistoryTimelineProps {
  items: OrderHistoryItem[] | undefined
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}

const HistoryItem: React.FC<{ item: OrderHistoryItem }> = ({ item }) => {
  const eventLabel = hasValue(item.eventLabel)
    ? item.eventLabel
    : hasValue(item.eventType)
      ? item.eventType
      : DASH

  const actorParts = [
    hasValue(item.actorTypeLabel) ? item.actorTypeLabel : item.actorType,
    item.actorName,
  ].filter(hasValue)
  const actorText = actorParts.length > 0 ? actorParts.join(' · ') : DASH

  const hasOldValue = hasValue(item.oldValue)
  const hasNewValue = hasValue(item.newValue)

  return (
    <li className="relative">
      <span
        aria-hidden="true"
        className="absolute -left-[31px] top-1.5 h-3 w-3 rounded-full border-2 border-white bg-gray-400"
      />
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">{eventLabel}</h3>
        <time className="text-xs text-gray-500">
          {hasValue(item.createdAtFormatted) ? item.createdAtFormatted : DASH}
        </time>
      </div>

      <p className="mt-1 text-xs text-gray-500">Инициатор: {actorText}</p>

      {hasValue(item.fieldName) && (
        <p className="mt-1 break-words text-xs text-gray-500">
          Поле: {item.fieldName}
        </p>
      )}

      {(hasOldValue || hasNewValue) && (
        <div className="mt-2 space-y-1 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-700">
          {hasOldValue && (
            <div className="break-words">
              <span className="text-gray-500">Было: </span>
              {item.oldValue}
            </div>
          )}
          {hasNewValue && (
            <div className="break-words">
              <span className="text-gray-500">Стало: </span>
              {item.newValue}
            </div>
          )}
        </div>
      )}

      {hasValue(item.comment) && (
        <p className="mt-2 whitespace-pre-line break-words text-sm text-gray-700">
          {item.comment}
        </p>
      )}

      {hasValue(item.operationId) && (
        <p className="mt-2 break-all text-xs text-gray-400">
          Операция: {item.operationId}
        </p>
      )}
    </li>
  )
}

/**
 * История изменений заказа по возрастанию времени. Только чтение.
 */
export const OrderHistoryTimeline: React.FC<OrderHistoryTimelineProps> = ({
  items,
  isLoading,
  isError,
  onRetry,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
        <span className="ml-3 text-sm text-gray-500">
          Загрузка истории событий...
        </span>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center py-8 text-center">
        <ExclamationTriangleIcon className="h-8 w-8 text-error-500" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          Не удалось загрузить историю событий
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Попробуйте запросить данные ещё раз
        </p>
        <Button
          className="mt-3"
          variant="secondary"
          size="sm"
          onClick={onRetry}
        >
          Повторить
        </Button>
      </div>
    )
  }

  if (!items || items.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
        Событий пока нет
      </div>
    )
  }

  return (
    <ol className="relative ml-2 space-y-6 border-l border-gray-200 pl-6">
      {items.map((item, index) => (
        <HistoryItem
          key={
            item.orderChangeHistoryId !== null
              ? `history-${item.orderChangeHistoryId}`
              : `history-${item.createdAt ?? 'unknown'}-${index}`
          }
          item={item}
        />
      ))}
    </ol>
  )
}
